import { useState } from "react";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import { checkUserId, checkNickname, addUser } from "../../api/userApi";
import { useToast } from "../../contexts/ToastContext";

export default function AddUser() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [formData, setFormData] = useState({
    userId: "",
    password: "",
    passwordConfirm: "",
    nickname: "",
    levelId: 1,
    avatarId: 1,
    regDate: new Date().toISOString().slice(0, 10), // YYYY-MM-DD
    exp: 0,
    isDelete: 0,
    gender: "",
  });

  const [errors, setErrors] = useState({});
  const [successMessages, setSuccessMessages] = useState({}); // 성공 메시지 상태
  const [focusedField, setFocusedField] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false); // 비밀번호 확인 표시 상태
  const [submitting, setSubmitting] = useState(false);

  // ------------------------------
  // 아이디 중복 검사 (서버 통신)
  // ------------------------------
  const debouncedCheckUserId = debounce(
    async (userId, setErrors, setSuccessMessages) => {
      if (!userId || userId.length < 4) return;

      try {
        const isDuplicate = await checkUserId(userId);

        if (isDuplicate) {
          setErrors((prev) => ({
            ...prev,
            userId: "이미 사용 중인 아이디 입니다.",
          }));
          setSuccessMessages((prev) => ({ ...prev, userId: "" }));
        } else {
          setErrors((prev) => ({ ...prev, userId: "" }));
          setSuccessMessages((prev) => ({
            ...prev,
            userId: "사용 가능한 아이디 입니다.",
          }));
        }
      } catch (e) {
        console.error("아이디 중복 확인 오류:", e);
      }
    },
    400
  );

  // ------------------------------
  // 닉네임 중복 검사 (서버 통신)
  // ------------------------------
  const debouncedCheckNickname = debounce(
    async (nickname, setErrors, setSuccessMessages) => {
      if (!nickname || nickname.length < 2) return;

      try {
        const isDuplicate = await checkNickname(nickname);

        if (isDuplicate) {
          setErrors((prev) => ({
            ...prev,
            nickname: "이미 사용 중인 닉네임 입니다.",
          }));
          setSuccessMessages((prev) => ({ ...prev, nickname: "" }));
        } else {
          setErrors((prev) => ({ ...prev, nickname: "" }));
          setSuccessMessages((prev) => ({
            ...prev,
            nickname: "사용 가능한 닉네임 입니다.",
          }));
        }
      } catch (e) {
        console.error("닉네임 중복 확인 오류:", e);
      }
    },
    400
  );

  const handleFocus = (name) => {
    setSuccessMessages((prev) => {
      const newSuccess = { ...prev };

      const fieldsToReset = ["userId", "nickname", "passwordConfirm"];

      fieldsToReset.forEach((fieldName) => {
        if (name !== fieldName) {
          newSuccess[fieldName] = "";
        }
      });

      return newSuccess;
    });

    setFocusedField(name);
    setErrors((prev) => ({ ...prev, [name]: "" }));
    if (successMessages[name]) {
      setSuccessMessages((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = () => setFocusedField("");

  const validateField = (name, value, currentFormData) => {
    let error = "";
    let success = "";
    const trimmedValue = value.trim();

    // 1. 아이디 검사
    if (name === "userId") {
      const id = value.trim();

      if (!id) {
        error = "아이디를 입력해주세요";
      } else if (!/^[a-zA-Z0-9]+$/.test(id)) {
        error = "아이디는 영문과 숫자만 사용할 수 있습니다";
      } else if (id.length < 4 || id.length > 20) {
        error = "아이디는 4~20자로 입력해주세요";
      } else {
        success = "사용 가능한 아이디 입니다.";
      }
    }

    // 2. 비밀번호 검사
    else if (name === "password") {
      if (!value) {
        error = "비밀번호를 입력해주세요";
      } else if (value.length < 4 || value.length > 12) {
        error = "비밀번호는 4~12자여야 합니다";
      } else {
        const types = [
          /[a-zA-Z]/.test(value),
          /[0-9]/.test(value),
          /[^a-zA-Z0-9]/.test(value),
        ];
        const typeCount = types.filter(Boolean).length;
        if (typeCount < 2)
          error = "영문, 숫자, 특수문자 중 최소 2개를 포함해야 합니다";
      }
    }
    // 3. 비밀번호 확인 검사
    else if (
      name === "passwordConfirm" &&
      currentFormData.password.length >= 4
    ) {
      // ✅ 수정 2 반영: 값이 있을 때만 에러 검사. 값이 없으면 에러/성공 메시지 모두 비움.
      if (value) {
        if (currentFormData.password !== value) {
          error = "비밀번호가 일치하지 않습니다";
        } else {
          success = "비밀번호가 일치합니다.";
        }
      }
    }
    // 4. 닉네임 검사
    else if (name === "nickname") {
      if (!trimmedValue) {
        error = "닉네임을 입력해주세요";
      } else if (trimmedValue.length > 10) {
        error = "닉네임은 최대 10자까지 입력할 수 있습니다";
      } else {
        success = "사용 가능한 닉네임 입니다.";
      }
    }

    // 에러가 있으면 성공 메시지는 반환하지 않음
    return { error, success: error ? "" : success };
  };

  /**
   * 입력 필드 값 변경 핸들러 (실시간 유효성 검사 적용)
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // 1. 폼 데이터 업데이트
    const newFormData = { ...formData, [name]: value };
    setFormData(newFormData);

    // 2. 실시간 유효성 검사 및 에러/성공 메시지 업데이트
    let updatedErrors = { ...errors };
    let updatedSuccess = { ...successMessages };

    if (name === "userId") {
      const { error, success } = validateField(name, value, newFormData);
      updatedErrors[name] = error;
      updatedSuccess[name] = success;

      // 유효성 오류 없을 때만 서버 중복 검사 실행
      if (!error && value.length >= 4 && value.length <= 20) {
        debouncedCheckUserId(value.trim(), setErrors, setSuccessMessages);
      }
    }

    if (name === "nickname") {
      const { error, success } = validateField(name, value, newFormData);
      updatedErrors[name] = error;
      updatedSuccess[name] = success;

      // 유효성 오류 없을 때만 서버 중복 검사 실행
      if (!error && value.trim().length >= 1 && value.trim().length <= 10) {
        debouncedCheckNickname(value.trim(), setErrors, setSuccessMessages);
      }
    }

    if (name !== "gender") {
      const { error, success } = validateField(name, value, newFormData);

      updatedErrors[name] = error;
      updatedSuccess[name] = success;

      // 비밀번호가 변경되면 비밀번호 확인 필드도 즉시 재검사
      if (name === "password" || name === "passwordConfirm") {
        const confirmResult = validateField(
          "passwordConfirm",
          newFormData.passwordConfirm,
          newFormData
        );
        updatedErrors.passwordConfirm = confirmResult.error;
        updatedSuccess.passwordConfirm = confirmResult.success;
      }
    } else {
      const fieldsToReset = ["userId", "nickname", "passwordConfirm"];
      let successReset = false; // 성공 메시지 초기화 발생 여부

      setSuccessMessages((prev) => {
        const newSuccess = { ...prev };
        fieldsToReset.forEach((fieldName) => {
          if (newSuccess[fieldName]) {
            newSuccess[fieldName] = "";
            successReset = true; // 초기화가 발생함
          }
        });
        return newSuccess;
      }); // 성별 자체의 에러 초기화

      if (errors.gender) updatedErrors.gender = ""; // 성공 메시지가 초기화되지 않았으면, 여기서 state를 업데이트합니다.

      if (!successReset) {
        setErrors(updatedErrors);
      }
      return;
    }

    setErrors(updatedErrors);
    setSuccessMessages(updatedSuccess);
  };

  /**
   * 폼 제출 시 최종 유효성 검사
   */
  const validateOnSubmit = () => {
    const finalErrors = {};

    // 1~4. 아이디, 비밀번호, 닉네임
    ["userId", "password", "nickname"].forEach((name) => {
      const result = validateField(name, formData[name], formData);
      if (result.error) finalErrors[name] = result.error;
    });

    // 4-1. 비밀번호 확인 최종 검사
    if (isPasswordValid) {
      if (!formData.passwordConfirm) {
        finalErrors.passwordConfirm = "비밀번호 확인을 입력해주세요"; // 빈 값인 경우 에러 처리
      } else {
        const confirmResult = validateField(
          "passwordConfirm",
          formData.passwordConfirm,
          formData
        );
        if (confirmResult.error)
          finalErrors.passwordConfirm = confirmResult.error;
      }
    }

    // 5. 성별 (제출 시 필수 검사)
    if (!formData.gender) finalErrors.gender = "성별을 선택해주세요";

    return finalErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalErrors = validateOnSubmit();

    if (Object.keys(finalErrors).length > 0) {
      setErrors(finalErrors);
      const firstErrorField = Object.keys(finalErrors)[0];
      document.querySelector(`[name="${firstErrorField}"]`)?.focus();

      showToast("입력된 정보를 다시 확인해주세요.", "e");
      return;
    }

    setSubmitting(true);
    showToast("회원가입 처리 중...", "i");

    try {
      await addUser(formData);
      showToast("회원가입 성공 🎉", "i");
      setTimeout(() => navigate("/login"), 1000);
    } catch (error) {
      console.error(error);

      showToast("회원가입 실패 또는 서버 오류가 발생했습니다.", "e");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => navigate(-1);
  const handleLogin = () => navigate("/login");

  // 비밀번호 유효성 확인 (비밀번호 확인 폼 활성화 조건)
  const isPasswordValid = formData.password.length >= 4 && !errors.password;

  // 전체 폼 유효성 확인 (가입 버튼 활성화 조건)
  const isFormValid =
    isPasswordValid &&
    !errors.userId &&
    !errors.passwordConfirm &&
    !errors.nickname &&
    !!formData.gender;

  // Toast 컴포넌트
  const Toast = ({ type, message }) => {
    if (!message) return null;
    const baseClasses =
      "fixed bottom-4 left-1/2 transform -translate-x-1/2 p-3 rounded-xl shadow-2xl text-white font-semibold z-50 transition-all duration-300";
    let colorClass = "";

    if (type === "success") colorClass = "bg-green-500";
    else if (type === "error") colorClass = "bg-red-500";
    else if (type === "info") colorClass = "bg-teal-500";

    return <div className={`${baseClasses} ${colorClass}`}>{message}</div>;
  };

  // 필드 테두리 및 색상 결정 로직을 추상화
  const getFieldClasses = (fieldName, hasSuccessMessage, hasError) => {
    const isFocused = focusedField === fieldName;

    if (hasError) {
      return "border-red-400 bg-red-50";
    }
    if (hasSuccessMessage) {
      return "border-teal-400"; // 성공 시 녹색 보더
    }
    if (isFocused) {
      return "border-teal-400 shadow-lg shadow-teal-100"; // 포커스 시 녹색 보더와 그림자
    }
    return "border-gray-200 hover:border-gray-300"; // 기본 상태
  };

  return (
    <div className="min-h-screen bg-white flex flex-col px-6 py-6 font-inter">
      {/* Toast 메시지 표시 */}
      <Toast />

      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleBack}
          className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition active:scale-95"
        >
          <svg
            className="w-6 h-6 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <h1
          className="text-2xl font-extrabold tracking-wide"
          style={{ color: "rgb(0, 224, 198)" }}
        >
          SINGSTEP
        </h1>

        <div className="w-6"></div>
      </div>

      {/* 가입 폼 */}
      <form onSubmit={handleSubmit} className="space-y-6 flex-1">
        {/* 아이디 */}
        <div>
          <label className="block text-sm font-bold text-gray-600 mb-2">
            아이디
          </label>
          <input
            type="text"
            name="userId"
            value={formData.userId}
            onChange={handleChange}
            onFocus={() => handleFocus("userId")}
            onBlur={handleBlur}
            placeholder="아이디 (4~20자, 영문·숫자)"
            className={`w-full px-4 py-4 border-2 text-base rounded-2xl bg-white transition-all focus:outline-none ${getFieldClasses(
              "userId",
              successMessages.userId,
              errors.userId
            )}`}
          />
          {errors.userId ? (
            <p className="text-sm text-red-500 mt-2">{errors.userId}</p>
          ) : successMessages.userId ? (
            <p
              className="text-sm mt-2 font-medium"
              style={{ color: "rgb(0, 224, 198)" }}
            >
              {successMessages.userId}
            </p>
          ) : null}
        </div>

        {/* 비밀번호 */}
        <div>
          <label className="block text-sm font-bold text-gray-600 mb-2">
            비밀번호
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              placeholder="비밀번호 (4~12자, 2종류 이상)"
              onChange={handleChange}
              onFocus={() => handleFocus("password")}
              onBlur={handleBlur}
              className={`w-full px-4 py-4 border-2 text-base rounded-2xl bg-white transition-all focus:outline-none ${
                errors.password
                  ? "border-red-400 bg-red-50"
                  : focusedField === "password"
                  ? "border-teal-400 shadow-lg shadow-teal-100"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-red-500 mt-2">{errors.password}</p>
          )}
        </div>

        {/* 비밀번호 확인 */}
        <div>
          <label className="block text-sm font-bold text-gray-600 mb-2">
            비밀번호 확인
          </label>
          <div className="relative">
            <input
              type={showPasswordConfirm ? "text" : "password"}
              name="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={handleChange}
              onFocus={() => handleFocus("passwordConfirm")}
              onBlur={handleBlur}
              placeholder={
                isPasswordValid ? "비밀번호 확인" : "비밀번호를 먼저 입력하세요"
              }
              disabled={!isPasswordValid} // 비활성화 조건
              className={`w-full px-4 py-4 border-2 text-base rounded-2xl transition-all focus:outline-none ${
                !isPasswordValid
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200" // 비활성화 스타일
                  : getFieldClasses(
                      "passwordConfirm",
                      successMessages.passwordConfirm,
                      errors.passwordConfirm
                    ) // 활성화 시 스타일 적용
              }`}
            />
            {/* 비밀번호 확인 폼 암호 보기/숨기기 기능 */}
            <button
              type="button"
              onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
              disabled={!isPasswordValid}
              className={`absolute right-4 top-1/2 -translate-y-1/2 ${
                !isPasswordValid
                  ? "text-gray-300"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              {showPasswordConfirm ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>
          {errors.passwordConfirm && (
            <p className="text-sm text-red-500 mt-2">
              {errors.passwordConfirm}
            </p>
          )}
          {successMessages.passwordConfirm && (
            <p
              className="text-sm mt-2 font-medium"
              style={{ color: "rgb(0, 224, 198)" }}
            >
              {successMessages.passwordConfirm}
            </p>
          )}
        </div>

        {/* 닉네임 */}
        <div>
          <label className="block text-sm font-bold text-gray-600 mb-2">
            닉네임
          </label>
          <input
            type="text"
            name="nickname"
            value={formData.nickname}
            onChange={handleChange}
            onFocus={() => handleFocus("nickname")}
            onBlur={handleBlur}
            placeholder="닉네임 (1~10자)"
            className={`w-full px-4 py-4 border-2 text-base rounded-2xl bg-white transition-all focus:outline-none ${getFieldClasses(
              "nickname",
              successMessages.nickname,
              errors.nickname
            )}`}
          />
          {errors.nickname ? (
            <p className="text-sm text-red-500 mt-2">{errors.nickname}</p>
          ) : successMessages.nickname ? (
            <p
              className="text-sm mt-2 font-medium"
              style={{ color: "rgb(0, 224, 198)" }}
            >
              {successMessages.nickname}
            </p>
          ) : null}
        </div>

        {/* 성별 선택 */}
        <div>
          <label className="block text-sm font-bold text-gray-600 mb-2">
            성별
          </label>
          <div
            className={`flex border-2 rounded-2xl overflow-hidden transition-all ${
              errors.gender
                ? "border-red-400"
                : formData.gender === "1" || formData.gender === "0"
                ? "border-teal-400"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            {/* 여자 (Value: "1") */}
            <label
              className={`flex-1 text-center py-3 cursor-pointer transition-all ${
                formData.gender === "1"
                  ? "bg-teal-100 font-bold text-gray-800"
                  : "bg-white text-gray-400 hover:bg-gray-50"
              }`}
              style={{
                borderRightWidth: "1px",
                borderRightColor:
                  formData.gender === "1"
                    ? "#2dd4be"
                    : formData.gender === "0"
                    ? "#e5e7eb"
                    : "#e5e7eb",
              }}
            >
              <input
                type="radio"
                name="gender"
                value="1"
                checked={formData.gender === "1"}
                onChange={handleChange}
                className="hidden"
              />
              여자
            </label>

            {/* 남자 (Value: "0") */}
            <label
              className={`flex-1 text-center py-3 cursor-pointer transition-all ${
                formData.gender === "0"
                  ? "bg-teal-100 font-bold text-gray-800"
                  : "bg-white text-gray-400 hover:bg-gray-50"
              }`}
              style={{
                borderLeftWidth: "1px",
                borderLeftColor:
                  formData.gender === "0"
                    ? "#2dd4be"
                    : formData.gender === "1"
                    ? "#e5e7eb"
                    : "#e5e7eb",
              }}
            >
              <input
                type="radio"
                name="gender"
                value="0"
                checked={formData.gender === "0"}
                onChange={handleChange}
                className="hidden"
              />
              남자
            </label>
          </div>
          {errors.gender && (
            <p className="text-sm text-red-500 mt-2">{errors.gender}</p>
          )}
        </div>

        {/* 가입 버튼 */}
        <button
          type="submit"
          disabled={!isFormValid || submitting}
          className={`w-full py-4 rounded-2xl text-white font-bold transition-all ${
            isFormValid
              ? "bg-teal-400 shadow-lg hover:shadow-xl active:scale-95"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          {submitting ? "가입 중..." : "가입하기"}
        </button>

        {/* 로그인 유도 */}
        <p className="text-center text-sm text-gray-500 mt-4">
          이미 계정이 있으신가요?{" "}
          <button
            type="button"
            className="font-bold"
            style={{ color: "rgb(0, 224, 198)" }}
            onClick={handleLogin}
          >
            로그인
          </button>
        </p>
      </form>
    </div>
  );
}
